import { http } from './http-client';
import type {
  AvailabilityResponse as CourtHoursResponse,
  BookingDetailDto,
  TrainerHoursOpening,
} from './generated';

import type {
  BookingSortKey,
  BookingSplitDetailsDto,
  CancelBookingResponse,
  CancelPreviewDto,
  CancelRecurringSeriesResponse,
  ClientSeriesListResponse,
  CreateRecurringBookingsResponse,
  RecurringConflict,
  ReschedulePreviewDto,
  SeriesPlanResponse,
  SortOrder,
  SplitBookingSummaryDto,
  BookingActivityItemDto as GenBookingActivityItemDto,
  BookingListItemDto as GenBookingListItemDto,
  BulkBookingActionPayload as GenBulkBookingActionPayload,
  CreateRecurringPayload as GenCreateRecurringPayload,
  ReschedulePayload,
} from './generated';

export type {
  BookingDetailDto,
  BookingSplitDetailsDto,
  CancelBookingResponse,
  CancelPreviewDto,
  CancelRecurringSeriesResponse,
  ClientSeriesListResponse,
  ReschedulePreviewDto,
  SeriesPlanResponse,
  SplitBookingSummaryDto,
};

export type BookingActivityItem = GenBookingActivityItemDto;
export type BookingListItem = GenBookingListItemDto;

import type { BookingStatus } from './schedule-api';
import type { SportCode } from './sports-api';
import type {
  BookingSplitSummary,
  CancelSplitByClubResponse,
  SplitParticipantStatus as GenSplitParticipantStatus,
  SplitPolicy as GenSplitPolicy,
  SplitStatus as GenSplitStatus,
  BookingTrainingPart,
  SwitchTrainingFormatResponse,
} from './generated';
import { TrainingFormat } from './generated';

export type RecurrenceFrequency = 'weekly' | 'biweekly' | 'monthly';

export type { BookingTrainingPart, SwitchTrainingFormatResponse };
export { TrainingFormat };

export interface BookingAddonInput {
  productId: string;
  quantity: number;
}

export interface BookingListParams {
  q?: string;
  status?: BookingStatus[];
  sport?: SportCode[];
  from?: string;
  to?: string;
  courtId?: string;
  clientId?: string;
  trainerId?: string;
  isRecurring?: boolean;
  includeCancelled?: boolean;
  hidePast?: boolean;
  sort?: `${BookingSortKey}`;
  order?: `${SortOrder}`;
  page?: number;
  pageSize?: number;
}

export interface BookingListResponse {
  items: BookingListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export type BookingActivityLogKind =
  | 'created'
  | 'deleted'
  | 'confirmed'
  | 'cancelled'
  | 'noShow'
  | 'checkedIn'
  | 'inProgress'
  | 'completed'
  | 'pendingPayment'
  | 'holding'
  | 'statusChanged'
  | 'updated';

export type CreateRecurringBookingPayload = Omit<GenCreateRecurringPayload, 'frequency' | 'conflictResolution'> & {
  frequency: RecurrenceFrequency;
  conflictResolution: 'skip' | 'abort';
};

export type BookingConflict = RecurringConflict;
export type CreateRecurringBookingResponse = CreateRecurringBookingsResponse;

export interface RescheduleBookingResult {
  penaltyChargedKopecks: number;
  isFreeReschedule: boolean;
  penaltyPercent: number;
  appliedTierMinHoursBefore: number | null;
  hoursUntilBooking: number;
  wasCharged: boolean;
  priceDeltaKopecks: number;
  newTotalPriceKopecks: number;
  owedKopecks: number;
  refundedToAdvanceKopecks: number;
  friendsRefundedKopecks: number;
  refundToCardKopecks: number;
  chargeSettledFromAdvanceKopecks: number;
  movePending: boolean;
  pendingMovePaymentId: string | null;
  pendingMovePayUrl: string | null;
  pendingMoveExpiresAt: string | null;
  onlineLinkSent: boolean;
}

export interface RescheduleObligationDto {
  paymentId: string;
  clientId: string;
  clientName: string;
  bookingId: string | null;
  amountKopecks: number;
  onlineLinkSent: boolean;
}

export type BookingBulkAction = 'cancel' | 'mark_no_show';

export type SplitPolicy = `${GenSplitPolicy}`;

export type SplitStatus = `${GenSplitStatus}`;

export type SplitParticipantStatus = `${GenSplitParticipantStatus}`;

export interface SplitParticipantDto {
  participantId: string;
  clientId: string;
  clientName: string;
  shareKopecks: number;
  status: SplitParticipantStatus;
  paymentMethod?: string | null;
  paidAt?: string | null;
}

export type BookingBulkRequest = Omit<GenBulkBookingActionPayload, 'ids' | 'action'> & {
  ids: string[];
  action: BookingBulkAction;
};

export interface BookingBulkResponse {
  updated: number;
  ids: string[];
  failed: { id: string; reason: string }[];
}

export const bookingApi = {
  switchTrainingFormat(
    id: string,
    format: TrainingFormat,
    participantIds: readonly string[],
  ): Promise<SwitchTrainingFormatResponse> {
    return http.post<SwitchTrainingFormatResponse>(`/bookings/${id}/training-format`, {
      format,
      participantIds,
    });
  },

  detail(id: string, call?: { signal?: AbortSignal }): Promise<BookingDetailDto> {
    return http.get<BookingDetailDto>(`/bookings/${id}`, { signal: call?.signal });
  },

  cancel(id: string, confirmed?: { expectedRefundKopecks?: number }): Promise<CancelBookingResponse> {
    return http.delete<CancelBookingResponse>(`/bookings/${id}`, {
      query: confirmed?.expectedRefundKopecks === undefined ? undefined : { expectedRefundKopecks: confirmed.expectedRefundKopecks },
    });
  },

  cancelPreview(id: string, call?: { signal?: AbortSignal }): Promise<CancelPreviewDto> {
    return http.get<CancelPreviewDto>(`/bookings/${id}/cancel-preview`, { signal: call?.signal });
  },

  reschedule(
    id: string,
    payload: ReschedulePayload,
  ): Promise<RescheduleBookingResult> {
    return http.patch<RescheduleBookingResult>(`/bookings/${id}/reschedule`, payload);
  },

  reschedulePreview(
    id: string,
    target?: { courtId: string },
    call?: { signal?: AbortSignal },
  ): Promise<ReschedulePreviewDto> {
    return http.get<ReschedulePreviewDto>(`/bookings/${id}/reschedule-preview`, {
      query: target ? { courtId: target.courtId } : undefined,
      signal: call?.signal,
    });
  },

  listRescheduleObligations(call?: { signal?: AbortSignal }): Promise<{ items: RescheduleObligationDto[] }> {
    return http.get<{ items: RescheduleObligationDto[] }>('/pos/sales/outstanding-surcharges', { signal: call?.signal });
  },

  requestExtend(
    id: string,
    pendingEndAt: string,
    openTrainerHours?: TrainerHoursOpening,
  ): Promise<void> {
    return http.patch(`/bookings/${id}/extend`, { pendingEndAt, openTrainerHours });
  },

  cancelExtend(id: string): Promise<void> {
    return http.delete(`/bookings/${id}/extend`);
  },

  noShow(id: string): Promise<void> {
    return http.patch(`/bookings/${id}/no-show`);
  },

  undoNoShow(id: string): Promise<void> {
    return http.delete(`/bookings/${id}/no-show`);
  },

  courtHours(date: string, sportType: string, courtId: string, trainerId: string | null, call?: { signal?: AbortSignal }): Promise<CourtHoursResponse> {
    return http.get<CourtHoursResponse>('/bookings/availability', {
      query: {
        date,
        sportType,
        durationMinutes: 60,
        resourceId: courtId,
        ...(trainerId ? { trainerId } : {}),
      },
      signal: call?.signal,
    });
  },

  list(params: BookingListParams, call?: { signal?: AbortSignal }): Promise<BookingListResponse> {
    return http.get<BookingListResponse>('/bookings', {
      query: {
        q: params.q,
        status: params.status?.join(','),
        sport: params.sport?.join(','),
        from: params.from,
        to: params.to,
        courtId: params.courtId,
        clientId: params.clientId,
        trainerId: params.trainerId,
        isRecurring: params.isRecurring,
        includeCancelled: params.includeCancelled,
        hidePast: params.hidePast,
        sort: params.sort,
        order: params.order,
        page: params.page,
        pageSize: params.pageSize,
      },
      signal: call?.signal,
    });
  },

  activity(id: string, call?: { signal?: AbortSignal }): Promise<{ items: BookingActivityItem[] }> {
    return http.get<{ items: BookingActivityItem[] }>(`/bookings/${id}/activity`, { signal: call?.signal });
  },

  createRecurring(payload: CreateRecurringBookingPayload): Promise<CreateRecurringBookingResponse> {
    return http.post<CreateRecurringBookingResponse>('/bookings/recurring', payload);
  },

  cancelSeries(seriesId: string): Promise<CancelRecurringSeriesResponse> {
    return http.delete<CancelRecurringSeriesResponse>(`/bookings/recurring/${seriesId}`);
  },

  seriesPlan(seriesId: string, call?: { signal?: AbortSignal }): Promise<SeriesPlanResponse> {
    return http.get<SeriesPlanResponse>(`/bookings/recurring/${seriesId}`, { signal: call?.signal });
  },

  clientSeries(clientId: string, call?: { signal?: AbortSignal }): Promise<ClientSeriesListResponse> {
    return http.get<ClientSeriesListResponse>('/bookings/recurring', { query: { clientId }, signal: call?.signal });
  },

  bulk(payload: BookingBulkRequest): Promise<BookingBulkResponse> {
    return http.post<BookingBulkResponse>('/bookings/bulk', payload);
  },

  async getSplit(bookingId: string, call?: { signal?: AbortSignal }): Promise<BookingSplitDetailsDto | null> {
    return (await http.get<BookingSplitDetailsDto | undefined>(`/bookings/${bookingId}/split`, { signal: call?.signal })) ?? null;
  },

  cancelSplitByClub(bookingId: string, reason?: string): Promise<CancelSplitByClubResponse> {
    return http.post<CancelSplitByClubResponse>(`/bookings/${bookingId}/split/cancel-by-club`, {
      reason: reason ?? null,
    });
  },
};
